import * as Phaser from 'phaser';
import { Preload } from './scenes/Preload';
import { MainMenuScene } from './scenes/MainMenuScene';
import { MainScene, type ActiveBossEncounterE2ESnapshot, type CombatFeedbackSmokeResult, type WeaponDamageSmokeResult } from './scenes/MainScene';
import type { MobileInputSnapshot } from './systems/MobileInputController';
import type { OrientationSnapshot } from './ui/OrientationOverlay';
import { GameOverScene } from './scenes/GameOverScene';
import { VictoryScene } from './scenes/VictoryScene';
import { RuntimeTestingModeState } from './config/RuntimeTestingMode';
import { getSharedProfileRuntime } from './managers/ProfileRuntime';
import { getAppVersionSnapshot } from './config/AppVersion';
import {
  createDebugCommandFailure,
  DebugCommandResult,
  DebugStatsSnapshot,
  ForceWaveDebugPayload,
  GiveWeaponDebugPayload,
  SkipWaveDebugPayload,
  SpawnEnemyDebugPayload
} from './systems/DebugCommandTypes';

// Конфигурация игры в соответствии с архитектурой проекта
const config: Phaser.Types.Core.GameConfig = {
  type: Phaser.AUTO,
  width: 1280,
  height: 720,
  backgroundColor: '#1a1a1a',
  physics: {
    default: 'arcade',
    arcade: {
      debug: false,
      gravity: { x: 0, y: 0 } // Для top-down survivor игры гравитация обычно не нужна
    }
  },
  scene: [Preload, MainMenuScene, MainScene, GameOverScene, VictoryScene],
  input: {
    activePointers: 4
  },
  // Дополнительные настройки, которые могут быть полезны для survivor игры
  scale: {
    parent: 'game',
    fullscreenTarget: 'game',
    mode: Phaser.Scale.RESIZE,
    width: '100%',
    height: '100%'
  },
  render: {
    pixelArt: false, // Для стилизованной 2D survivor игры может быть полезен pixelArt: true
    antialias: true
  }
};

type PhaserE2EApi = {
  getActiveScenes: () => string[];
  runWeaponDamageSmoke: (weaponId: string) => Promise<WeaponDamageSmokeResult>;
  runProjectileCountSmoke: (weaponId: string, targetLevel: number) => ReturnType<MainScene['runProjectileCountSmokeForE2E']>;
  runTintSmoke: () => ReturnType<MainScene['runTintSmokeForE2E']>;
  runRewardSmoke: (mode: 'ordinary' | 'evolution') => ReturnType<MainScene['runRewardSmokeForE2E']> | null;
  showUpgradeChoices: () => boolean;
  getUpgradeChoicesSnapshot: () => ReturnType<MainScene['getUpgradeChoicesSnapshotForE2E']>;
  forceNextBossEncounter: () => {
    spawned: boolean;
    encounterId?: string;
    reason?: 'testing-disabled' | 'active-encounter' | 'no-remaining-encounters';
  };
  getActiveBossEncounter: () => ActiveBossEncounterE2ESnapshot | null;
  killActiveBoss: () => {
    killed: boolean;
    encounterId?: string;
    reason?: 'no-active-encounter' | 'boss-sprite-missing';
  };
  getRuntimeTestingState: () => RuntimeTestingModeState | null;
  setRuntimeTestingMode: (patch: Partial<RuntimeTestingModeState>) => RuntimeTestingModeState | null;
  isDiagnosticsOverlayVisible: () => boolean;
  getDiagnosticsSnapshot: () => ReturnType<MainScene['getDiagnosticsSnapshot']> | null;
  getMobileControlsSnapshot: () => MobileInputSnapshot | { active: false; reason: 'main-scene-not-active' | 'scene-not-ready' };
  getOrientationSnapshot: () => OrientationSnapshot | { active: false; reason: 'main-scene-not-active' | 'scene-not-ready' };
  triggerCombatFeedbackSmoke: () => CombatFeedbackSmokeResult | null;
  runCombatLifecycleSmoke: () => ReturnType<MainScene['runCombatLifecycleSmokeForE2E']>;
  getProfileSnapshot: () => ReturnType<ReturnType<typeof getSharedProfileRuntime>['getProfileSnapshot']>;
  clearProfileState: () => ReturnType<ReturnType<typeof getSharedProfileRuntime>['clearProfileState']>;
  spawnEnemy: (payload?: unknown) => DebugCommandResult<{
    enemyId: string;
    x: number;
    y: number;
    activeEnemyCount: number;
    usedDefaultCoordinates: boolean;
  }>;
  giveWeapon: (payload?: unknown) => DebugCommandResult<unknown>;
  skipWave: (payload?: unknown) => DebugCommandResult<unknown>;
  forceWave: (payload?: unknown) => DebugCommandResult<unknown>;
  getDebugStatsSnapshot: () => DebugCommandResult<DebugStatsSnapshot>;
  getAppVersion: () => ReturnType<typeof getAppVersionSnapshot>;
  getSceneVersionLabel: (sceneKey: 'MainMenu' | 'VictoryScene' | 'GameOverScene') => string | null;
};

declare global {
  interface Window {
    __PHASER_E2E__?: PhaserE2EApi;
  }
}

// Инициализация игры
const game = new Phaser.Game(config);

const isObjectPayload = (payload: unknown): payload is Record<string, unknown> => (
  typeof payload === 'object' && payload !== null && !Array.isArray(payload)
);

const createInvalidPayloadFailure = <T>(
  command: 'spawnEnemy' | 'giveWeapon' | 'skipWave' | 'forceWave' | 'getDebugStatsSnapshot',
  message: string,
  payload: unknown
): DebugCommandResult<T> => {
  return createDebugCommandFailure(command, 'e2e', 'invalid-payload', message, {
    payloadType: payload === null ? 'null' : Array.isArray(payload) ? 'array' : typeof payload
  });
};

if (new URLSearchParams(window.location.search).has('e2e')) {
  const profileRuntime = getSharedProfileRuntime();
  const getSceneVersionLabel = (sceneKey: 'MainMenu' | 'VictoryScene' | 'GameOverScene'): string | null => {
    const scene = game.scene.getScene(sceneKey);
    if (!scene || !game.scene.isActive(sceneKey)) {
      return null;
    }

    const versionObject = scene.children?.getByName('app-version-label');
    if (!versionObject || !('text' in versionObject)) {
      return null;
    }

    return typeof versionObject.text === 'string' ? versionObject.text : null;
  };

  const withMainSceneDebugCommand = <T>(
    command: 'spawnEnemy' | 'giveWeapon' | 'skipWave' | 'forceWave' | 'getDebugStatsSnapshot',
    callback: (mainScene: MainScene) => DebugCommandResult<T>
  ): DebugCommandResult<T> => {
    if (!game.scene.isActive('Main')) {
      return {
        ok: false,
        command,
        source: 'e2e',
        reason: 'main-scene-not-active'
      };
    }

    const scene = game.scene.getScene('Main');
    if (!(scene instanceof MainScene)) {
      return {
        ok: false,
        command,
        source: 'e2e',
        reason: 'main-scene-not-active'
      };
    }

    return callback(scene);
  };

  window.__PHASER_E2E__ = {
    runCombatLifecycleSmoke: () => {
      const scene = game.scene.getScene('Main');
      return scene instanceof MainScene && game.scene.isActive('Main') ? scene.runCombatLifecycleSmokeForE2E() : null;
    },
    getActiveScenes: () => game.scene.getScenes(true).map((scene) => scene.scene.key),
    runProjectileCountSmoke: (weaponId, targetLevel) => {
      const scene = game.scene.getScene('Main');
      return scene instanceof MainScene && game.scene.isActive('Main') ? scene.runProjectileCountSmokeForE2E(weaponId, targetLevel) : null;
    },
    runTintSmoke: async () => {
      const scene = game.scene.getScene('Main');
      return scene instanceof MainScene && game.scene.isActive('Main') ? scene.runTintSmokeForE2E() : null;
    },
    runRewardSmoke: mode => {
      const scene = game.scene.getScene('Main');
      return scene instanceof MainScene && game.scene.isActive('Main') ? scene.runRewardSmokeForE2E(mode) : null;
    },
    showUpgradeChoices: () => {
      const scene = game.scene.getScene('Main');
      return scene instanceof MainScene && game.scene.isActive('Main') ? scene.showUpgradeChoicesForE2E() : false;
    },
    getUpgradeChoicesSnapshot: () => {
      const scene = game.scene.getScene('Main');
      return scene instanceof MainScene && game.scene.isActive('Main') ? scene.getUpgradeChoicesSnapshotForE2E() : null;
    },
    runWeaponDamageSmoke: async (weaponId: string) => {
      const mainScene = game.scene.getScene('Main');
      if (mainScene instanceof MainScene) {
        return mainScene.runWeaponDamageSmoke(weaponId);
      }

      return {
        weaponId,
        appeared: false,
        damaged: false,
        startHealth: 0,
        endHealth: 0,
        error: 'main-scene-not-active'
      };
    },
    forceNextBossEncounter: () => {
      const mainScene = game.scene.getScene('Main');
      return mainScene instanceof MainScene
        ? mainScene.forceNextBossEncounterForE2E()
        : { spawned: false, reason: 'testing-disabled' as const };
    },
    getActiveBossEncounter: () => {
      const mainScene = game.scene.getScene('Main');
      return mainScene instanceof MainScene ? mainScene.getActiveBossEncounterForE2E() : null;
    },
    killActiveBoss: () => {
      const mainScene = game.scene.getScene('Main');
      return mainScene instanceof MainScene
        ? mainScene.killActiveBossForE2E()
        : { killed: false, reason: 'no-active-encounter' as const };
    },
    getRuntimeTestingState: () => {
      const mainScene = game.scene.getScene('Main');
      return mainScene instanceof MainScene ? mainScene.getRuntimeTestingModeState() : null;
    },
    setRuntimeTestingMode: (patch: Partial<RuntimeTestingModeState>) => {
      const mainScene = game.scene.getScene('Main');
      return mainScene instanceof MainScene ? mainScene.setRuntimeTestingModePatch(patch, 'e2e') : null;
    },
    isDiagnosticsOverlayVisible: () => {
      const mainScene = game.scene.getScene('Main');
      return mainScene instanceof MainScene ? mainScene.isDiagnosticsOverlayVisible() : false;
    },
    getDiagnosticsSnapshot: () => {
      const mainScene = game.scene.getScene('Main');
      return mainScene instanceof MainScene && game.scene.isActive('Main') ? mainScene.getDiagnosticsSnapshot() : null;
    },
    getMobileControlsSnapshot: () => {
      const mainScene = game.scene.getScene('Main');
      return mainScene instanceof MainScene && game.scene.isActive('Main')
        ? mainScene.getMobileControlsSnapshotForE2E()
        : { active: false, reason: 'main-scene-not-active' as const };
    },
    getOrientationSnapshot: () => {
      const mainScene = game.scene.getScene('Main');
      return mainScene instanceof MainScene && game.scene.isActive('Main')
        ? mainScene.getOrientationSnapshotForE2E()
        : { active: false, reason: 'main-scene-not-active' as const };
    },
    triggerCombatFeedbackSmoke: () => {
      const mainScene = game.scene.getScene('Main');
      return mainScene instanceof MainScene ? mainScene.triggerCombatFeedbackSmokeForE2E() : null;
    },
    getProfileSnapshot: () => {
      return profileRuntime.getProfileSnapshot();
    },
    clearProfileState: () => {
      return profileRuntime.clearProfileState();
    },
    spawnEnemy: (payload?: unknown) => {
      if (payload !== undefined && !isObjectPayload(payload)) {
        return createInvalidPayloadFailure('spawnEnemy', 'spawnEnemy payload must be an object.', payload);
      }

      return withMainSceneDebugCommand('spawnEnemy', (mainScene) => mainScene.spawnEnemyForDebug({
        ...(payload as SpawnEnemyDebugPayload | undefined),
        source: 'e2e'
      }));
    },
    giveWeapon: (payload?: unknown) => {
      if (payload !== undefined && !isObjectPayload(payload)) {
        return createInvalidPayloadFailure('giveWeapon', 'giveWeapon payload must be an object.', payload);
      }

      return withMainSceneDebugCommand('giveWeapon', (mainScene) => mainScene.giveWeaponForDebug({
        ...(payload as GiveWeaponDebugPayload | undefined),
        source: 'e2e'
      }));
    },
    skipWave: (payload?: unknown) => {
      if (payload !== undefined && !isObjectPayload(payload)) {
        return createInvalidPayloadFailure('skipWave', 'skipWave payload must be an object.', payload);
      }

      return withMainSceneDebugCommand('skipWave', (mainScene) => mainScene.skipWaveForDebug({
        ...(payload as SkipWaveDebugPayload | undefined),
        source: 'e2e'
      }));
    },
    forceWave: (payload?: unknown) => {
      if (payload !== undefined && !isObjectPayload(payload)) {
        return createInvalidPayloadFailure('forceWave', 'forceWave payload must be an object.', payload);
      }

      return withMainSceneDebugCommand('forceWave', (mainScene) => mainScene.forceWaveForDebug(
        payload ? { ...(payload as unknown as ForceWaveDebugPayload), source: 'e2e' } : undefined
      ));
    },
    getDebugStatsSnapshot: () => {
      return withMainSceneDebugCommand('getDebugStatsSnapshot', (mainScene) => mainScene.getDebugStatsSnapshot({
        source: 'e2e'
      }));
    },
    getAppVersion: () => getAppVersionSnapshot(),
    getSceneVersionLabel
  };
}
